/**
 * Data Transformation Utilities for Budget Data
 *
 * Transforms flat backend API responses (MovieBudgetResponse)
 * to hierarchical frontend display structure (BudgetPhase[])
 *
 * Backend structure: MovieBudget → items[] (flat list with id_buget_step)
 * Frontend structure: BudgetPhase[] → topics[] → lineItems[]
 *
 * Phase mapping (from MovieBudgetSteps table):
 * 1 = Desenvolvimento do Projeto
 * 2 = Pré-Produção
 * 3 = Produção
 * 4 = Pós-Produção
 * 5 = Comercialização
 *
 * Note: Backend has no "topic" concept - items are grouped by description pattern
 */

import {
  MovieBudgetResponse,
  MovieBudgetItemResponse,
  BudgetPhase,
  BudgetTopic,
  BudgetLineItem,
  PhaseChartData,
  BudgetTableStepResponse,
  BudgetTableItemResponse,
} from "@/types/orcamentos.interface";

/**
 * Phase name mapping from id_buget_step to display name
 */
const PHASE_NAMES: Record<number, string> = {
  1: "Desenvolvimento do Projeto",
  2: "Pré-Produção",
  3: "Produção",
  4: "Pós-Produção",
  5: "Comercialização",
};

/**
 * Additional phase names that may exist (for flexibility)
 */
const ADDITIONAL_PHASE_NAMES: Record<number, string> = {
  6: "Despesas Administrativas",
  7: "Tributos e Taxas",
  8: "Gerenciamento",
};

/**
 * Get phase name from phase ID
 */
export function getPhaseName(phaseId: number): string {
  return PHASE_NAMES[phaseId] || ADDITIONAL_PHASE_NAMES[phaseId] || `Fase ${phaseId}`;
}

/**
 * Format date from ISO string to Brazilian format (dd/mm/yyyy)
 */
function formatDateBR(dateString: string | null): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("pt-BR");
}

/**
 * Format ISO date string for display
 */
function formatISODate(dateString: string | null): string {
  if (!dateString) return new Date().toISOString();
  return dateString;
}

/**
 * Parse decimal string to number (Prisma returns Decimal as string)
 */
function parseDecimal(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  return parseFloat(value) || 0;
}

/**
 * Calculate percentage (executed / approved * 100)
 */
function calculatePercentage(executed: number, approved: number): number {
  if (approved === 0) return 0;
  return Math.round((executed / approved) * 100);
}

/**
 * Extract topic name from item description
 * Uses the first part before " - " or the full description
 * Example: "Banco de Dados - Servidor" → "Banco de Dados"
 */
function extractTopicName(description: string): string {
  if (!description) return "Geral";
  const parts = description.split(" - ");
  return parts[0].trim() || "Geral";
}

/**
 * Transform a single MovieBudgetItemResponse to BudgetLineItem
 */
function transformItemToLineItem(
  item: MovieBudgetItemResponse,
  approvedBudget: number = 0
): BudgetLineItem {
  const value = parseDecimal(item.amount);
  const orcamentoPrevisto = approvedBudget > 0 ? approvedBudget : value;

  return {
    id: String(item.id),
    name: item.description,
    orcamentoPrevisto: orcamentoPrevisto,
    value: value,
    percentual: calculatePercentage(value, orcamentoPrevisto),
    lastUpdate: formatDateBR(item.payment_expected_till),
  };
}

/**
 * Group items by topic name and transform to BudgetTopic[]
 */
function groupItemsIntoTopics(
  items: MovieBudgetItemResponse[],
  phaseId: string
): BudgetTopic[] {
  // Group items by topic name (extracted from description)
  const topicMap = new Map<string, MovieBudgetItemResponse[]>();

  items.forEach((item) => {
    const topicName = extractTopicName(item.description);
    const existing = topicMap.get(topicName) || [];
    existing.push(item);
    topicMap.set(topicName, existing);
  });

  // Transform to BudgetTopic[]
  const topics: BudgetTopic[] = [];
  let topicIndex = 1;

  topicMap.forEach((topicItems, topicName) => {
    const lineItems = topicItems.map((item) => transformItemToLineItem(item));

    // Calculate topic totals
    const orcamentoExecutado = lineItems.reduce((sum, li) => sum + li.value, 0);
    const orcamentoAprovado = lineItems.reduce((sum, li) => sum + li.orcamentoPrevisto, 0);

    topics.push({
      id: `${phaseId}-${topicIndex}`,
      name: topicName,
      orcamentoExecutado,
      orcamentoAprovado,
      percentual: calculatePercentage(orcamentoExecutado, orcamentoAprovado),
      lineItems,
    });

    topicIndex++;
  });

  return topics;
}

/**
 * Transform MovieBudgetResponse to BudgetPhase[]
 *
 * This is the main transformation function that converts the flat
 * backend response into the hierarchical structure expected by the UI
 */
export function transformBudgetToPhases(budget: MovieBudgetResponse): BudgetPhase[] {
  // Always create an array of 8 phases
  const phases: BudgetPhase[] = [];

  // Create a map to securely lookup items
  const phaseMap = new Map<number, MovieBudgetItemResponse[]>();

  if (budget && budget.items) {
    budget.items.forEach((item) => {
      const phaseId = item.id_buget_step;
      const existing = phaseMap.get(phaseId) || [];
      existing.push(item);
      phaseMap.set(phaseId, existing);
    });
  }

  // Iterate exactly 1 through 8
  for (let phaseId = 1; phaseId <= 8; phaseId++) {
    const phaseItems = phaseMap.get(phaseId) || [];
    const phaseIdStr = String(phaseId);

    // Group items into topics
    const topics = groupItemsIntoTopics(phaseItems, phaseIdStr);

    // Calculate phase totals from topics
    const orcamentoExecutado = topics.reduce((sum, t) => sum + t.orcamentoExecutado, 0);
    const orcamentoAprovado = topics.reduce((sum, t) => sum + t.orcamentoAprovado, 0);

    phases.push({
      id: phaseIdStr,
      phaseName: getPhaseName(phaseId),
      orcamentoExecutado,
      orcamentoAprovado,
      percentual: calculatePercentage(orcamentoExecutado, orcamentoAprovado),
      topics,
      iniciadoEm: formatISODate(budget?.captation_till || null),
      atualizadoEm: formatISODate(budget?.date_of_approve || null),
    });
  }

  return phases;
}

/**
 * Transform BudgetPhase[] to PhaseChartData[] for horizontal bar chart
 */
export function transformPhasesToChartData(phases: BudgetPhase[]): PhaseChartData[] {
  return phases.map((phase) => ({
    name: phase.phaseName,
    value: phase.orcamentoAprovado,
  }));
}

/**
 * Transform multiple budgets (from movie-budgets endpoint) to phases
 * Combines all budgets' items into a single phase list
 */
export function transformBudgetsToPhasesMultiple(
  budgets: MovieBudgetResponse[]
): BudgetPhase[] {
  if (!budgets || budgets.length === 0) {
    return [];
  }

  // Combine all items from all budgets
  const allItems: MovieBudgetItemResponse[] = [];
  budgets.forEach((budget) => {
    if (budget.items) {
      allItems.push(...budget.items);
    }
  });

  // Create a synthetic budget with all items
  const combinedBudget: MovieBudgetResponse = {
    ...budgets[0],
    items: allItems,
  };

  return transformBudgetToPhases(combinedBudget);
}

/**
 * Get line items for a specific phase (flat list)
 */
export function getLineItemsForPhase(
  budget: MovieBudgetResponse,
  phaseId: number
): BudgetLineItem[] {
  if (!budget || !budget.items) return [];

  const phaseItems = budget.items.filter((item) => item.id_buget_step === phaseId);
  return phaseItems.map((item) => transformItemToLineItem(item));
}

/**
 * Calculate total budget metrics
 */
export function calculateBudgetTotals(phases: BudgetPhase[]): {
  totalExecutado: number;
  totalAprovado: number;
  percentual: number;
} {
  const totalExecutado = phases.reduce((sum, p) => sum + p.orcamentoExecutado, 0);
  const totalAprovado = phases.reduce((sum, p) => sum + p.orcamentoAprovado, 0);

  return {
    totalExecutado,
    totalAprovado,
    percentual: calculatePercentage(totalExecutado, totalAprovado),
  };
}

/**
 * Find a specific Topic (Subitem) by ID across all phases
 */
export function findTopicById(
  phases: BudgetPhase[],
  topicId: string
): { phase: BudgetPhase; topic: BudgetTopic } | null {
  for (const phase of phases) {
    const topic = phase.topics.find((t) => t.id === topicId);
    if (topic) {
      return { phase, topic };
    }
  }
  return null;
}

/**
 * Find a specific line item (Payment) by ID across all phases
 */
export function findLineItemById(
  phases: BudgetPhase[],
  itemId: string
): { phase: BudgetPhase; topic: BudgetTopic; item: BudgetLineItem } | null {
  for (const phase of phases) {
    for (const topic of phase.topics) {
      const item = topic.lineItems.find((li) => li.id === itemId);
      if (item) {
        return { phase, topic, item };
      }
    }
  }
  return null;
}

/**
 * Transform BudgetTableStepResponse[] (from /budget/{budgetId}/table)
 * to BudgetPhase[] hierarchical structure
 */
export function transformBudgetTableToPhases(tableData: BudgetTableStepResponse[]): BudgetPhase[] {
  // Always create an array of 8 phases
  const phases: BudgetPhase[] = [];

  // Create a map to securely lookup steps
  const phaseMap = new Map<number, BudgetTableStepResponse>();

  if (tableData && Array.isArray(tableData)) {
    tableData.forEach((step) => {
      phaseMap.set(step.stepId, step);
    });
  }

  // Iterate exactly 1 through 8
  for (let phaseId = 1; phaseId <= 8; phaseId++) {
    const stepData = phaseMap.get(phaseId);
    const phaseIdStr = String(phaseId);

    const topics: BudgetTopic[] = [];

    if (stepData && stepData.items) {
      stepData.items.forEach((item) => {
        const itemAmount = parseDecimal(item.amount);

        // Sum execution values from children (payments)
        let executedValue = 0;
        const lineItems: BudgetLineItem[] = [];

        // Check for direct payments mapping from recent backend updates
        if (item.payments && Array.isArray(item.payments)) {
          item.payments.forEach((payment) => {
            const paymentAmount = parseDecimal(payment.amount);
            executedValue += paymentAmount;
            lineItems.push({
              id: `payment-${payment.id}`,
              name: payment.staff_name
                ? `${payment.staff_name}${payment.staff_document ? ` - ${payment.staff_document}` : ''}`
                : (payment.notes || 'Pagamento'),
              orcamentoPrevisto: paymentAmount, // Payments don't have a distinct previsto vs executed usually, just value
              value: paymentAmount,
              percentual: calculatePercentage(paymentAmount, itemAmount),
              lastUpdate: formatDateBR(payment.emission_date || payment.payment_expected_till),
              fiscalDocType: payment.fiscal_doc_type,
              fiscalDocNumber: payment.fiscal_doc_number,
              _rawData: payment,
            } as BudgetLineItem & { _rawData?: any });
          });
        } else {
          // Fallback to legacy structure via staffContracts
          executedValue = item.staffContracts?.reduce((contractSum, contract) => {
            const paymentsSum = contract.payments?.reduce((paySum, payment) => {
              return paySum + parseDecimal(payment.amount);
            }, 0) || 0;
            return contractSum + paymentsSum;
          }, 0) || 0;

          item.staffContracts?.forEach((contract) => {
            contract.payments?.forEach((payment) => {
              const paymentAmount = parseDecimal(payment.amount);
              lineItems.push({
                id: `payment-${payment.id}`,
                name: payment.staff_name
                  ? `${payment.staff_name}${payment.staff_document ? ` - ${payment.staff_document}` : ''}`
                  : (payment.notes || 'Pagamento'),
                orcamentoPrevisto: paymentAmount, // Payments don't have a distinct previsto vs executed usually, just value
                value: paymentAmount,
                percentual: calculatePercentage(paymentAmount, itemAmount),
                lastUpdate: formatDateBR(payment.emission_date || payment.payment_expected_till),
                fiscalDocType: payment.fiscal_doc_type,
                fiscalDocNumber: payment.fiscal_doc_number,
                _rawData: payment,
              } as BudgetLineItem & { _rawData?: any });
            });
          });
        }

        topics.push({
          id: String(item.id),
          name: item.description,
          orcamentoExecutado: executedValue,
          orcamentoAprovado: itemAmount,
          percentual: calculatePercentage(executedValue, itemAmount),
          lineItems,
        });
      });
    }

    // Calculate phase totals from topics
    const orcamentoExecutado = topics.reduce((sum, t) => sum + t.orcamentoExecutado, 0);
    const orcamentoAprovado = topics.reduce((sum, t) => sum + t.orcamentoAprovado, 0);

    phases.push({
      id: phaseIdStr,
      phaseName: getPhaseName(phaseId),
      orcamentoExecutado,
      orcamentoAprovado,
      percentual: calculatePercentage(orcamentoExecutado, orcamentoAprovado),
      topics,
      iniciadoEm: formatISODate(null), // The table response doesn't bring budget meta info like dates
      atualizadoEm: formatISODate(null),
    });
  }

  return phases;
}
